import { useState, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { normalizeDomain } from './domains'

/**
 * The chip editor for `client.allowedDomains`.
 *
 * A plain comma-separated text field was the alternative and is worse: a domain list is a SET,
 * and a set rendered as a string invites a stray comma that silently creates `cnx.com ` as a
 * distinct entry. Chips make each member a thing you can see and remove.
 *
 * Enter, Tab, comma and space all commit — people type all four — and Backspace on an empty
 * box removes the last chip, which is the behaviour every other tag input in the world has.
 */
export function AllowedDomainsField({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id: string
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  const [draft, setDraft] = useState('')

  const commit = (raw: string) => {
    const domain = normalizeDomain(raw)
    setDraft('')
    if (!domain || value.includes(domain)) return
    onChange([...value, domain])
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',' || event.key === ' ') {
      if (!draft.trim()) return
      // Enter inside a dialog would submit the form, and a comma would land in the box.
      event.preventDefault()
      commit(draft)
      return
    }
    if (event.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-card p-1.5',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        disabled && 'pointer-events-none opacity-60',
      )}
    >
      {value.map((domain) => (
        <span
          key={domain}
          className="flex items-center gap-1 rounded-sm bg-brand-soft py-0.5 pr-1 pl-2 text-xs font-medium text-brand-dark"
        >
          {domain}
          <button
            type="button"
            aria-label={`Remove ${domain}`}
            onClick={() => onChange(value.filter((d) => d !== domain))}
            className="grid size-4 place-items-center rounded-xs text-brand-dark/70 hover:bg-brand hover:text-text-inverse"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <Input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        // Committing on blur too: a domain typed and then clicked away from is still a
        // domain the person meant to add, and losing it silently is the classic tag-input bug.
        onBlur={() => draft.trim() && commit(draft)}
        placeholder={value.length === 0 ? 'cnx.com' : 'Add another…'}
        className="h-7 min-w-32 flex-1 border-0 bg-transparent px-1.5 text-sm shadow-none focus-visible:ring-0"
      />
    </div>
  )
}
